import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateDateRangeType, updateCustomDateRange, setWeekRange, setMonthRange } from "../../../state/reducers";
import { ManualSyncModal } from "../../manualSync";
import {
  format,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  differenceInDays,
  getISOWeek,
  getISOWeekYear,
  startOfISOWeek,
} from "date-fns";

export const DateRangeSelector = ({ onRefresh, onExport, loading, exportLoading }) => {
  const dispatch = useDispatch();
  const { dateRangeType, customDateRange } = useSelector((state) => state.dateRange);
  const [activeTab, setActiveTab] = useState(dateRangeType);
  const [showManualSync, setShowManualSync] = useState(false);

  const handleDateInputClick = (inputRef) => {
    if (inputRef && inputRef.showPicker) {
      inputRef.showPicker();
    }
  };

  const handleTabChange = useCallback(
    (type) => {
      setActiveTab(type);

      const today = new Date();

      if (type === "weekly") {
        // Set current week
        dispatch(setWeekRange(today));
      } else if (type === "monthly") {
        // Set current month
        dispatch(setMonthRange(today));
      } else if (type === "custom") {
        // Set default 30-day range for custom
        const thirtyDaysAgo = subDays(today, 29);
        dispatch(
          updateCustomDateRange({
            from: format(thirtyDaysAgo, "yyyy-MM-dd"),
            to: format(today, "yyyy-MM-dd"),
          })
        );
        dispatch(updateDateRangeType(type));
      } else {
        // Daily - set to today
        dispatch(
          updateCustomDateRange({
            from: format(today, "yyyy-MM-dd"),
            to: format(today, "yyyy-MM-dd"),
          })
        );
        dispatch(updateDateRangeType(type));
      }
    },
    [dispatch]
  );

  const handlePrevious = useCallback(() => {
    if (loading) return;

    const { from, to } = customDateRange;

    switch (dateRangeType) {
      case "daily":
        const prevDay = subDays(new Date(from), 1);
        dispatch(
          updateCustomDateRange({
            from: format(prevDay, "yyyy-MM-dd"),
            to: format(prevDay, "yyyy-MM-dd"),
          })
        );
        break;

      case "weekly":
        const prevWeekStart = subWeeks(new Date(from), 1);
        const prevWeekEnd = subWeeks(new Date(to), 1);
        dispatch(
          updateCustomDateRange({
            from: format(prevWeekStart, "yyyy-MM-dd"),
            to: format(prevWeekEnd, "yyyy-MM-dd"),
          })
        );
        break;

      case "monthly":
        const prevMonthStart = subMonths(new Date(from), 1);
        const prevMonthEnd = subMonths(new Date(to), 1);
        dispatch(
          updateCustomDateRange({
            from: format(prevMonthStart, "yyyy-MM-dd"),
            to: format(prevMonthEnd, "yyyy-MM-dd"),
          })
        );
        break;

      case "custom":
        const daysDiff = differenceInDays(new Date(to), new Date(from));
        const newCustomStart = subDays(new Date(from), daysDiff + 1);
        const newCustomEnd = subDays(new Date(from), 1);
        dispatch(
          updateCustomDateRange({
            from: format(newCustomStart, "yyyy-MM-dd"),
            to: format(newCustomEnd, "yyyy-MM-dd"),
          })
        );
        break;
    }
  }, [dateRangeType, customDateRange, dispatch, loading]);

  const handleNext = useCallback(() => {
    if (loading) return;

    const { from, to } = customDateRange;

    switch (dateRangeType) {
      case "daily":
        const nextDay = addDays(new Date(from), 1);
        dispatch(
          updateCustomDateRange({
            from: format(nextDay, "yyyy-MM-dd"),
            to: format(nextDay, "yyyy-MM-dd"),
          })
        );
        break;

      case "weekly":
        const nextWeekStart = addWeeks(new Date(from), 1);
        const nextWeekEnd = addWeeks(new Date(to), 1);
        dispatch(
          updateCustomDateRange({
            from: format(nextWeekStart, "yyyy-MM-dd"),
            to: format(nextWeekEnd, "yyyy-MM-dd"),
          })
        );
        break;

      case "monthly":
        const nextMonthStart = addMonths(new Date(from), 1);
        const nextMonthEnd = addMonths(new Date(to), 1);
        dispatch(
          updateCustomDateRange({
            from: format(nextMonthStart, "yyyy-MM-dd"),
            to: format(nextMonthEnd, "yyyy-MM-dd"),
          })
        );
        break;

      case "custom":
        const daysDiff = differenceInDays(new Date(to), new Date(from));
        const newCustomStart = addDays(new Date(to), 1);
        const newCustomEnd = addDays(new Date(to), daysDiff + 1);
        dispatch(
          updateCustomDateRange({
            from: format(newCustomStart, "yyyy-MM-dd"),
            to: format(newCustomEnd, "yyyy-MM-dd"),
          })
        );
        break;
    }
  }, [dateRangeType, customDateRange, dispatch, loading]);

  const formatDateRange = () => {
    const { from, to } = customDateRange;
    if (from === to) {
      return format(new Date(from), "MMMM dd, yyyy");
    }
    return `${format(new Date(from), "MMM dd, yyyy")} - ${format(new Date(to), "MMM dd, yyyy")}`;
  };

  const getCurrentWeek = () => {
    const today = new Date();
    const monday = startOfISOWeek(today);
    const year = getISOWeekYear(monday);
    const weekNumber = getISOWeek(monday);
    return `${year}-W${String(weekNumber).padStart(2, "0")}`;
  };

  const handleWeekSelection = (e) => {
    const [year, week] = e.target.value.split("-W");
    const date = new Date(year, 0, 1 + (week - 1) * 7);
    const monday = startOfISOWeek(date);
    const sunday = endOfWeek(monday);

    dispatch(
      updateCustomDateRange({
        from: format(monday, "yyyy-MM-dd"),
        to: format(sunday, "yyyy-MM-dd"),
      })
    );
  };

  const getCurrentMonth = () => {
    const today = new Date();
    return format(today, "yyyy-MM");
  };

  const handleMonthSelection = (e) => {
    const [year, month] = e.target.value.split("-");
    const monthStart = startOfMonth(new Date(year, month - 1, 1));
    const monthEnd = endOfMonth(monthStart);

    dispatch(
      updateCustomDateRange({
        from: format(monthStart, "yyyy-MM-dd"),
        to: format(monthEnd, "yyyy-MM-dd"),
      })
    );
  };

  return (
    <div>
      {/* Tabs */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ul className="nav nav-pills">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "daily" ? "active" : ""}`}
              onClick={() => handleTabChange("daily")}
              disabled={loading}
            >
              Daily
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "weekly" ? "active" : ""}`}
              onClick={() => handleTabChange("weekly")}
              disabled={loading}
            >
              Weekly
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "monthly" ? "active" : ""}`}
              onClick={() => handleTabChange("monthly")}
              disabled={loading}
            >
              Monthly
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "custom" ? "active" : ""}`}
              onClick={() => handleTabChange("custom")}
              disabled={loading}
            >
              Custom
            </button>
          </li>
        </ul>

        <div className="btn-list">
          <button
            className="btn btn-icon btn-outline-primary"
            onClick={() => setShowManualSync(true)}
            disabled={loading}
            title="Manual Sync"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-refresh" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
              <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
            </svg>
          </button>
          <button
            className="btn btn-primary"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh Data"}
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={onExport}
            disabled={loading || exportLoading}
          >
            {exportLoading ? "Exporting..." : "Export"}
          </button>
        </div>
      </div>

      {/* Manual Sync Modal */}
      <ManualSyncModal
        show={showManualSync}
        onClose={() => setShowManualSync(false)}
      />

      {/* Tab Content */}
      <div className="mt-3">
        {dateRangeType === "daily" && (
          <div className="row g-2 align-items-center">
            <div className="col-auto">
              <label className="form-label mb-0">Select Date</label>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-primary btn-icon"
                onClick={handlePrevious}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="15 6 9 12 15 18" /></svg>
              </button>
            </div>
            <div className="col-auto" style={{ maxWidth: "200px" }}>
              <input
                type="date"
                className="form-control cursor-pointer"
                value={customDateRange.from}
                onChange={(e) =>
                  dispatch(
                    updateCustomDateRange({
                      from: e.target.value,
                      to: e.target.value,
                    })
                  )
                }
                onClick={(e) => handleDateInputClick(e.target)}
                disabled={loading}
                style={{ cursor: "pointer" }}
              />
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-primary btn-icon"
                onClick={handleNext}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="9 6 15 12 9 18" /></svg>
              </button>
            </div>
          </div>
        )}

        {dateRangeType === "weekly" && (
          <div className="row g-2 align-items-center">
            <div className="col-auto">
              <label className="form-label mb-0">Select Week</label>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-primary btn-icon"
                onClick={handlePrevious}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="15 6 9 12 15 18" /></svg>
              </button>
            </div>
            <div className="col-auto" style={{ maxWidth: "200px" }}>
              <input
                type="week"
                className="form-control"
                defaultValue={getCurrentWeek()}
                value={(() => {
                  const monday = startOfISOWeek(new Date(customDateRange.from));
                  const year = getISOWeekYear(monday);
                  const weekNumber = getISOWeek(monday);
                  return `${year}-W${String(weekNumber).padStart(2, "0")}`;
                })()}
                onChange={handleWeekSelection}
                onClick={(e) => handleDateInputClick(e.target)}
                disabled={loading}
                style={{ cursor: "pointer" }}
              />
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-primary btn-icon"
                onClick={handleNext}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="9 6 15 12 9 18" /></svg>
              </button>
            </div>
          </div>
        )}

        {dateRangeType === "monthly" && (
          <div className="row g-2 align-items-center">
            <div className="col-auto">
              <label className="form-label mb-0">Select Month</label>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-primary btn-icon"
                onClick={handlePrevious}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="15 6 9 12 15 18" /></svg>
              </button>
            </div>
            <div className="col-auto" style={{ maxWidth: "200px" }}>
              <input
                type="month"
                className="form-control"
                defaultValue={getCurrentMonth()}
                value={(() => {
                  const today = new Date(customDateRange.from);
                  const year = today.getFullYear();
                  const month = today.getMonth() + 1;
                  return `${year}-${String(month).padStart(2, "0")}`;
                })()}
                onChange={handleMonthSelection}
                onClick={(e) => handleDateInputClick(e.target)}
                disabled={loading}
                style={{ cursor: "pointer" }}
              />
            </div>
            <div className="col-auto">
              <button
                className="btn btn-outline-primary btn-icon"
                onClick={handleNext}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="9 6 15 12 9 18" /></svg>
              </button>
            </div>
          </div>
        )}

        {dateRangeType === "custom" && (
          <div className="row g-3">
            <div className="col-auto">
              <label className="form-label">From</label>
              <div className="input-group">
                <button
                  className="btn btn-outline-primary"
                  onClick={handlePrevious}
                  disabled={loading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="15 6 9 12 15 18" /></svg>
                </button>
                <input
                  type="date"
                  className="form-control"
                  value={customDateRange.from}
                  onChange={(e) =>
                    dispatch(
                      updateCustomDateRange({
                        ...customDateRange,
                        from: e.target.value,
                      })
                    )
                  }
                  onClick={(e) => handleDateInputClick(e.target)}
                  disabled={loading}
                  style={{ cursor: "pointer", maxWidth: "160px" }}
                />
              </div>
            </div>
            <div className="col-auto">
              <label className="form-label">To</label>
              <div className="input-group">
                <input
                  type="date"
                  className="form-control"
                  value={customDateRange.to}
                  onChange={(e) =>
                    dispatch(
                      updateCustomDateRange({
                        ...customDateRange,
                        to: e.target.value,
                      })
                    )
                  }
                  onClick={(e) => handleDateInputClick(e.target)}
                  disabled={loading}
                  style={{ cursor: "pointer", maxWidth: "160px" }}
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={handleNext}
                  disabled={loading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="9 6 15 12 9 18" /></svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Date Range Display */}
      <div className="mt-3">
        <span className="badge bg-primary-lt p-2">{formatDateRange()}</span>
      </div>
    </div>
  );
};
